import {Quill} from "vue2-editor";
import axios from "@/api/axiom-instance";
import config from "@/config";

const BlockEmbed = Quill.import('blots/block/embed');
const Video = Quill.import('formats/video');

class DividerBlot extends BlockEmbed {}

DividerBlot.blotName = 'divider';
DividerBlot.tagName = 'hr';

class ResponsiveVideo extends Video {
  static create(value) {
    const node = super.create(value);
    node.setAttribute('loading', 'lazy');
    node.setAttribute('allowfullscreen', 'true');
    node.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    node.removeAttribute('width');
    node.removeAttribute('height');
    return node;
  }

  static formats(domNode) {
    const formats = super.formats(domNode);
    delete formats.width;
    delete formats.height;
    return formats;
  }

  format(name, value) {
    if (name === 'width' || name === 'height') return;
    super.format(name, value);
  }
}

ResponsiveVideo.blotName = 'video';
ResponsiveVideo.className = 'ql-video';
ResponsiveVideo.tagName = 'IFRAME';

Quill.register(DividerBlot, true);
Quill.register(ResponsiveVideo, true);

export const EDITOR_TOOLBAR = [
  ['bold', 'italic', 'underline'],
  [{list: 'ordered'}, {list: 'bullet'}],
  [{align: ''}, {align: 'center'}, {align: 'right'}],
  ['link', 'image', 'video'],
  ['clean']
];

const IMAGE_WIDTHS = [
  {label: '25 %', value: '25%'},
  {label: '50 %', value: '50%'},
  {label: '75 %', value: '75%'},
  {label: '100 %', value: '100%'},
  {label: 'Původní', value: null}
];

const EDITOR_IMAGE_TYPE = 'editor-image';
const EDITOR_IMAGE_ACCEPT = 'image/jpeg, image/png';

function editorImageName() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function editorImageUrl(name) {
  return `${config.API_URL}/image?type=${EDITOR_IMAGE_TYPE}&name=${name}`;
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function setupImageUpload(quill) {
  quill.getModule('toolbar').addHandler('image', () => {
    const range = quill.getSelection(true);

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = EDITOR_IMAGE_ACCEPT;

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;

      const name = editorImageName();

      try {
        const data = await readAsDataUrl(file);
        await axios.post('/image', {name, type: EDITOR_IMAGE_TYPE, data});
        const target = quill.getSelection() || range;
        quill.insertEmbed(target.index, 'image', editorImageUrl(name), Quill.sources.USER);
        quill.setSelection(target.index + 1, Quill.sources.SILENT);
      } catch (e) {
        window.alert('Obrázek se nepodařilo nahrát, zkus to prosím znovu.');
      }
    };

    input.click();
  });
}

function setupImageResize(quill) {
  const container = quill.root.parentNode;

  const panel = document.createElement('div');
  panel.style.cssText = [
    'position:absolute', 'display:none', 'z-index:20',
    'background:#fff', 'border:1px solid #ccc', 'border-radius:3px',
    'box-shadow:0 1px 4px rgba(0,0,0,0.25)', 'padding:2px', 'white-space:nowrap'
  ].join(';');

  let target = null;

  const position = () => {
    if (!target) return;
    const c = container.getBoundingClientRect();
    const i = target.getBoundingClientRect();
    panel.style.top = (i.top - c.top + 4) + 'px';
    panel.style.left = (i.left - c.left + 4) + 'px';
  };

  const hide = () => {
    target = null;
    panel.style.display = 'none';
  };

  const show = (img) => {
    target = img;
    const current = img.getAttribute('width') || null;
    buttons.forEach(({el, value}) => {
      const active = value === current;
      el.style.background = active ? '#e6e6e6' : 'transparent';
      el.style.fontWeight = active ? '700' : '400';
    });
    panel.style.display = 'block';
    position();
  };

  const buttons = IMAGE_WIDTHS.map(({label, value}) => {
    const el = document.createElement('button');
    el.type = 'button';
    el.textContent = label;
    el.title = 'Šířka obrázku';
    el.style.cssText = [
      'border:0', 'background:transparent', 'padding:2px 6px',
      'font-size:11px', 'line-height:16px', 'cursor:pointer', 'color:#444'
    ].join(';');

    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      if (!target) return;
      const blot = Quill.find(target);
      if (!blot) return;
      quill.formatText(quill.getIndex(blot), 1, {width: value}, Quill.sources.USER);
      show(target);
    });

    panel.appendChild(el);
    return {el, value};
  });

  container.appendChild(panel);

  quill.root.addEventListener('click', (e) => {
    if (e.target && e.target.tagName === 'IMG') show(e.target);
    else hide();
  });

  quill.root.addEventListener('scroll', position);

  quill.on('selection-change', (range) => {
    if (range === null) hide();
  });

  quill.on('text-change', () => {
    if (target && !target.isConnected) hide();
    else position();
  });
}

export function setupEditorToolbars(refs, refNames = ['editorCs', 'editorEn']) {
  refNames.forEach(refName => {
    const ref = refs[refName];
    if (!ref || !ref.quill) return;
    const quill = ref.quill;
    const toolbarContainer = quill.getModule('toolbar').container;

    let savedRange = null;
    quill.on('selection-change', (range) => {
      if (range) savedRange = range;
    });

    const colorGroup = document.createElement('span');
    colorGroup.classList.add('ql-formats');
    colorGroup.title = 'Barva textu';

    const colorLabel = document.createElement('span');
    colorLabel.style.cssText = 'font-size:11px;line-height:24px;margin-right:3px;color:#444;';
    colorLabel.textContent = 'A';

    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.value = '#152326';
    colorInput.style.cssText = [
      'width:20px', 'height:20px', 'padding:1px 2px',
      'border:1px solid #ccc', 'border-radius:3px',
      'cursor:pointer', 'vertical-align:middle'
    ].join(';');

    colorInput.addEventListener('change', (e) => {
      if (savedRange !== null) {
        quill.setSelection(savedRange.index, savedRange.length, Quill.sources.SILENT);
      }
      quill.format('color', e.target.value, Quill.sources.USER);
    });

    colorGroup.appendChild(colorLabel);
    colorGroup.appendChild(colorInput);
    toolbarContainer.appendChild(colorGroup);

    const dividerGroup = document.createElement('span');
    dividerGroup.classList.add('ql-formats');

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.title = 'Oddělovací čára';
    btn.innerHTML = [
      '<svg viewBox="0 0 18 18" width="18" height="18">',
      '<line x1="1" y1="9" x2="17" y2="9"',
      ' stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>',
      '</svg>'
    ].join('');

    btn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, 'divider', true, Quill.sources.USER);
      quill.setSelection(range.index + 1, Quill.sources.SILENT);
    });

    dividerGroup.appendChild(btn);
    toolbarContainer.appendChild(dividerGroup);

    setupImageUpload(quill);
    setupImageResize(quill);
  });
}
